import { cn } from "@/lib/cn";
import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from "react";
import { SURFACE_BACKGROUND } from "./surface-background";
import { useElementRef } from "./use-element-ref";

/** Background a piece of text sits on: the page colour, a card-like surface or the prize games' fixed paper. */
export type SurfaceKind = "page" | "card" | "game" | "night";

const SurfaceContext = createContext<SurfaceKind | null>(null);

/** Surface set by the nearest provider, or `null` when no surface was established yet. */
export function useSurfaceContext(): SurfaceKind | null {
  return useContext(SurfaceContext);
}

/** Surface text resolves against; `card` outside any provider keeps the legacy card-text default. */
export function useSurface(): SurfaceKind {
  return useContext(SurfaceContext) ?? "card";
}

/** Props of `SurfaceProvider`. */
export type SurfaceProviderProps = { surface: SurfaceKind; children?: ReactNode };

/** Declares the surface its subtree is painted on so Heading/Text tones follow it; renders no element. */
export function SurfaceProvider({ surface, children }: SurfaceProviderProps) {
  return <SurfaceContext.Provider value={surface}>{children}</SurfaceContext.Provider>;
}

const SURFACE_TEXT: Record<SurfaceKind, string> = { page: "text-page-text", card: "text-card-text", game: "text-game-ink", night: "text-night-ink" };

/** Type a surface sets for its subtree: the game surface switches to the game body face. */
const SURFACE_TYPE: Partial<Record<SurfaceKind, string>> = { game: "font-game-body" };

/** Props of `Surface`; `className` carries layout, radius and elevation, never another background. */
export type SurfaceProps = HTMLAttributes<HTMLElement> & {
  surface: SurfaceKind;
  /** Also sets the surface text colour, for children that inherit it (raw text, `Text` without `tone`). */
  ink?: boolean;
  as?: "div" | "section" | "header" | "aside" | "nav" | "article" | "span" | "li" | "ul";
};

/** Element painted with a surface background that also declares it, so text inside never reads the wrong tone. */
export const Surface = forwardRef<HTMLElement, SurfaceProps>(function Surface(
  { surface, ink = false, as: Tag = "div", className, children, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  return (
    <Tag {...rest} ref={elementRef} className={cn(SURFACE_BACKGROUND[surface], SURFACE_TYPE[surface], ink && SURFACE_TEXT[surface], className)}>
      <SurfaceProvider surface={surface}>{children}</SurfaceProvider>
    </Tag>
  );
});
